// BarGraph6.js
import React from "react";

export const BarGraph6 = ({ data }) => {
  if (!data) return null;

  const {
    caption = "",
    title = "",
    xAxis = { label: "", categories: [] }, // e.g., ["April", "May", "June", "July", "August", "September"]
    yAxis = { label: "", ticks: [] },       // e.g., ticks: [350,300,250,200,150,100,50,0]
    lines = [],                             // Array of line objects, each with a "name" and "values" (6 numbers)
    colors = []                             // e.g., ["#1f77b4", "#ff7f0e"]
  } = data;

  // Define chart dimensions (based on the sample SVG)
  const svgWidth = 450;
  const svgHeight = 476.67;
  
  // Margins and chart area
  const xLeft = 90;       // left margin (approximate from sample)
  const xRight = 435;     // right end of chart area
  const chartWidth = xRight - xLeft; // e.g., 345
  
  const yTop = 72;        // top of chart area (max value)
  const yBottom = 272;    // x-axis (min value)
  const chartHeight = yBottom - yTop; // e.g., 200
  
  // y-axis scale function:
  // value=350  -> y = yTop (72)
  // value=0    -> y = yBottom (272)
  const scaleY = (value) => yBottom - (value / 350) * chartHeight;
  
  // Calculate x positions for each month (6 points)
  // Equally spaced along chartWidth
  const numMonths = xAxis.categories.length; // should be 6
  const xSpacing = chartWidth / (numMonths - 1);
  const xPositions = xAxis.categories.map((_, i) => xLeft + i * xSpacing);
  
  // Render Lines: For each line, build a polyline connecting data points.
  const renderLines = () => {
    return lines.map((line, lineIndex) => {
      // Compute points for the polyline
      const points = line.values
        .map((val, i) => {
          const x = xPositions[i];
          const y = scaleY(val);
          return `${x},${y}`;
        })
        .join(" ");
      
      return (
        <polyline
          key={`line-${lineIndex}`}
          points={points}
          fill="none"
          stroke={colors[lineIndex] || "#000"}
          strokeWidth="2"
        />
      );
    });
  };

  // Render markers (circles) at each data point for each line
  const renderMarkers = () => {
    return lines.map((line, lineIndex) => {
      return line.values.map((val, i) => {
        const x = xPositions[i];
        const y = scaleY(val);
        return (
          <circle
            key={`marker-${lineIndex}-${i}`}
            cx={x}
            cy={y}
            r="3"
            fill={colors[lineIndex] || "#000"}
            stroke="#fff"
            strokeWidth="1"
          />
        );
      });
    });
  };

  // Render Y-Axis: Draw grid lines and labels
  const renderYAxis = () => (
    <g>
      <text
        fill="#000"
        fontFamily="Crimson Text"
        fontSize="19.84"
        textAnchor="middle"
        transform="translate(40 172) rotate(-90)"
      >
        {yAxis.label}
      </text>
      {/* Y-axis line */}
      <line
        x1={xLeft}
        y1={yTop}
        x2={xLeft}
        y2={yBottom}
        stroke="#000"
        strokeWidth="0.9"
      />
      {yAxis.ticks.map((tick) => {
        const yPos = scaleY(tick);
        return (
          <g key={`y-tick-${tick}`}>
            <line
              x1={xLeft}
              y1={yPos}
              x2={xRight}
              y2={yPos}
              stroke="#ccc"
              strokeWidth="0.5"
            />
            <text
              x={xLeft - 10}
              y={yPos + 6}
              fill="#000"
              fontFamily="Crimson Text"
              fontSize="19.84"
              textAnchor="end"
            >
              {tick}
            </text>
          </g>
        );
      })}
    </g>
  );

  // Render X-Axis: Draw axis line and month labels.
  const renderXAxis = () => (
    <g>
      <line
        x1={xLeft}
        y1={yBottom}
        x2={xRight}
        y2={yBottom}
        stroke="#000"
        strokeWidth="0.9"
      />
      {xAxis.categories.map((month, i) => (
        <text
          key={`x-label-${i}`}
          x={xPositions[i]}
          y={yBottom + 25}
          fill="#000"
          fontFamily="Crimson Text"
          fontSize="19.84"
          textAnchor="middle"
        >
          {month}
        </text>
      ))}
      <text
        x={(xLeft + xRight) / 2}
        y={yBottom + 50}
        fill="#000"
        fontFamily="Crimson Text"
        fontSize="19.84"
        textAnchor="middle"
      >
        {xAxis.label}
      </text>
    </g>
  );

  // Render Graph Title (centered at the top)
  const renderTitle = () => (
    <text
      x={svgWidth / 2}
      y="40"
      fill="#000"
      fontFamily="Crimson Text"
      fontSize="19.84"
      textAnchor="middle"
    >
      {title}
    </text>
  );

  // Render Legend for the lines
  const renderLegend = () => {
    if (!colors || colors.length < lines.length) return null;
    return (
      <g>
        <rect
          x="150"
          y="380"
          width="150"
          height="50"
          fill="none"
          stroke="#000"
          strokeWidth="0.9"
        />
        {lines.map((line, i) => {
          const legendX = 160;
          const legendY = 395 + i * 20;
          return (
            <g key={`legend-${i}`}>
              <rect
                x={legendX}
                y={legendY}
                width="12"
                height="12"
                fill={colors[i]}
                stroke="#000"
                strokeWidth="0.9"
              />
              <text
                x={legendX + 20}
                y={legendY + 12}
                fill="#000"
                fontFamily="Crimson Text"
                fontSize="19.84"
              >
                {line.name}
              </text>
            </g>
          );
        })}
      </g>
    );
  };

  return (
    <figure className="image">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={svgWidth}
        height={svgHeight}
        viewBox={`0 0 ${svgWidth} ${svgHeight}`}
        role="img"
        aria-label={caption}
      >
        <g id="ed420550-79eb-48d4-af01-cd27cdd08afd">
          <defs>
            {/* (Optional: add markers if needed) */}
          </defs>
          {renderYAxis()}
          {renderXAxis()}
          {renderLines()}
          {renderMarkers()}
          {renderTitle()}
          {renderLegend()}
        </g>
      </svg>
    </figure>
  );
};

export default BarGraph6;
